/**
 * apiClient.js
 * Central fetch wrapper for all API calls.
 *
 * - Adds Authorization: Bearer <token> header (from the OIDC session)
 * - Adds X-Correlation-ID header for request tracing
 * - Throws a structured error with the response body on non-2xx status
 * - On 401, attempts silent token renewal then retries once before redirecting to login
 *
 * Setup � call both once from ProtectedApp render body:
 *   configureApiClient(() => auth.user?.access_token);
 *   configureUnauthorizedHandler(() => auth.signinSilent().catch(() => auth.signinRedirect()));
 */

// -- Token provider ------------------------------------------------------------
let _getToken = () => undefined;

export function configureApiClient(tokenFn) {
  if (typeof tokenFn !== 'function') {
    console.warn('[apiClient] configureApiClient expects a function, got:', typeof tokenFn);
    return;
  }
  _getToken = tokenFn;
}

// -- Unauthorized (401) handler ------------------------------------------------
let _onUnauthorized = null;

/**
 * Wire in a handler that is called when any API request returns 401.
 * It should attempt a silent token renewal and fall back to a full redirect.
 *
 * Call this from ProtectedApp render body:
 *   configureUnauthorizedHandler(() =>
 *     auth.signinSilent().catch(() => auth.signinRedirect())
 *   );
 */
export function configureUnauthorizedHandler(fn) {
  if (typeof fn === 'function') _onUnauthorized = fn;
}

// -- Core fetch helper ---------------------------------------------------------
async function request(method, path, body, _isRetry = false) {
  const token = _getToken();

  const headers = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': crypto.randomUUID(),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // -- 401 Unauthorized: token expired or rejected ---------------------------
  // Attempt silent renewal once, then retry the request with the fresh token.
  // If silent renewal fails (e.g. refresh token also expired), the handler
  // falls back to a full signinRedirect so the user is sent to login.
  if (response.status === 401 && !_isRetry && _onUnauthorized) {
    console.warn('[apiClient] 401 � attempting silent token renewal');
    try {
      await _onUnauthorized();
      return request(method, path, body, true);
    } catch (renewErr) {
      console.error('[apiClient] Silent renewal failed:', renewErr.message);
      // signinRedirect was already triggered by the handler's catch fallback
    }
  }

  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { message: response.statusText };
    }
    console.error(`[apiClient] ${method} ${path} ? ${response.status}`, errorBody);
    const err = new Error(errorBody?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.response = errorBody;
    throw err;
  }

  if (response.status === 204) return null;

  const text = await response.text();
  if (!text || text.trim() === '') return null;

  try {
    return JSON.parse(text);
  } catch (e) {
    console.error(`[apiClient] Failed to parse JSON from ${path}:`, e.message);
    return null;
  }
}

// -- Public API ----------------------------------------------------------------
const apiClient = {
  get:    (path)       => request('GET',    path),
  post:   (path, body) => request('POST',   path, body),
  put:    (path, body) => request('PUT',    path, body),
  delete: (path)       => request('DELETE', path),
};

export default apiClient;
